import { useCallback, useState } from "react"
import { useRouter } from "next/navigation"
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { toast } from "sonner"

import { Customize as OrderCustomize } from "@/lib/api/order-service"
import { getCartClient } from "@/lib/client/api/cart"
import { getOrderDetailClient } from "@/lib/client/api/order"
import { getRestaurantDetail } from "@/lib/client/api/restaurant"
import { useAuthStore } from "@/lib/store/auth-store"
import { MenuItem } from "@/lib/types"
import { RestaurantCart, RestaurantItem } from "@/lib/types/cart"
import { useRestaurantDetailLongCache } from "@/hooks/use-restaurant-dish-map"
import { clearCart as clearCartAction } from "@/app/actions/cart"
import { addRestaurantItems } from "@/app/actions/order"

import { reconcileCartWithDishMap } from "../utils/cart-reconciliation"

export const useCartDetail = () => {
  return useQuery({
    queryKey: ["cart"],
    queryFn: getCartClient,
    placeholderData: keepPreviousData,
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: true,
  })
}

// Hook to get item quantities for a specific restaurant
export const useCartItems = (restaurantId: string | number) => {
  const { data: cartResponse, isLoading } = useCartDetail()

  // Find the cart for this restaurant
  const restaurantCart = cartResponse?.carts?.find(
    (cart) => cart.restaurant_id === Number(restaurantId)
  )

  // Get item quantities
  const itemQuantities = new Map<number, number>()

  if (restaurantCart) {
    restaurantCart.restaurant_items.forEach((item) => {
      itemQuantities.set(item.item_id, item.quantity)
    })
  }

  // Return a function to get quantity for a specific item
  const getItemQuantity = (itemId: number): number => {
    return itemQuantities.get(itemId) || 0
  }

  return {
    getItemQuantity,
    isLoading,
    restaurantCart,
  }
}

// Interface for the new mutation variables
interface UpdateByIndexVariables {
  restaurantId: string | number
  itemIndex: number
  newQuantity: number
  cartId: string | number // cartId is essential
}

export const useCartActions = () => {
  // Local state for cart actions
  const [isEditing, setIsEditing] = useState(false)
  const [selectedCarts, setSelectedCarts] = useState<number[]>([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [cartsToDelete, setCartsToDelete] = useState<number[]>([])

  const queryClient = useQueryClient()
  const userId = useAuthStore((state) => state.user?.user_id)
  const { data: cartResponse } = useCartDetail()

  // Delete cart mutation
  const deleteMutation = useMutation({
    mutationFn: async (cartIds: number[]) => {
      if (!userId) {
        throw new Error("User not authenticated")
      }

      // Convert cart IDs to strings before calling the action
      return clearCartAction(
        userId.toString(),
        cartIds.map((id) => id.toString())
      )
    },
    onSuccess: () => {
      toast.success("Đã xóa giỏ hàng", {
        description: "Giỏ hàng đã được xóa thành công",
      })

      // Invalidate cart query to refresh data
      queryClient.invalidateQueries({ queryKey: ["cart"] })
      setIsEditing(false)
      setSelectedCarts([])
      setDeleteDialogOpen(false)
    },
    onError: () => {
      toast.error("Không thể xóa giỏ hàng", {
        description: "Đã xảy ra lỗi khi xóa giỏ hàng. Vui lòng thử lại sau.",
      })
    },
  })

  // Update item quantity mutation
  const updateItemQuantityMutation = useMutation({
    mutationFn: async ({
      restaurantId,
      itemId,
      newQuantity,
      cartId,
    }: {
      restaurantId: number | string
      itemId: number
      newQuantity: number
      cartId?: string | number
    }) => {
      if (!userId) {
        throw new Error("User not authenticated")
      }

      // Get the current cart
      const currentCart = cartResponse?.carts?.find(
        (cart) => cart.restaurant_id === Number(restaurantId)
      )

      if (!currentCart) {
        throw new Error("Cart not found")
      }

      // Prepare all cart items with the updated quantity
      const updatedItems = currentCart.restaurant_items
        .map((item) => {
          // Convert customizations to the required format for the API
          const customizes: OrderCustomize[] = (item.customizes || []).map(
            (customize) => ({
              id: customize.id.toString(),
              name: customize.name,
              options: customize.options.map((option) => ({
                id: option.id.toString(),
                name: option.name,
                price: option.price,
                quantity: option.quantity,
              })),
            })
          )

          if (item.item_id === itemId) {
            return {
              item_id: item.item_id.toString(),
              quantity: newQuantity,
              price: item.price,
              note: item.note || "",
              customizes: customizes,
            }
          }
          return {
            item_id: item.item_id.toString(),
            quantity: item.quantity,
            price: item.price,
            note: item.note || "",
            customizes: customizes,
          }
        })
        .filter((item) => item.quantity > 0) // Remove items with zero quantity

      // Call the API to update the cart
      return addRestaurantItems(
        userId.toString(),
        restaurantId.toString(),
        updatedItems,
        {
          cartId: cartId?.toString() || currentCart.cart_id?.toString(),
        }
      )
    },
    onSuccess: () => {
      // Invalidate cart query to refresh data
      queryClient.invalidateQueries({ queryKey: ["cart"] })
    },
    onError: (error) => {
      toast.error("Không thể cập nhật số lượng", {
        description:
          error instanceof Error
            ? error.message
            : "Đã xảy ra lỗi khi cập nhật số lượng. Vui lòng thử lại sau.",
      })
    },
  })

  // New mutation for updating by index
  const updateItemQuantityByIndexMutation = useMutation({
    mutationFn: async (variables: UpdateByIndexVariables) => {
      const { restaurantId, itemIndex, newQuantity, cartId } = variables
      if (!userId) throw new Error("User not authenticated")

      const currentCart = cartResponse?.carts?.find(
        (cart) => cart.restaurant_id === Number(restaurantId)
      )
      if (!currentCart) {
        toast.error("Giỏ hàng không tìm thấy để cập nhật bằng index.")
        throw new Error("Cart not found for index-based update")
      }

      // Deep clone to avoid direct state mutation issues with the cache
      const clonedItems = JSON.parse(
        JSON.stringify(currentCart.restaurant_items)
      ) as RestaurantItem[]

      if (itemIndex < 0 || itemIndex >= clonedItems.length) {
        toast.error("Sản phẩm không hợp lệ để cập nhật (index out of bounds).")
        throw new Error("Item index out of bounds for update")
      }

      if (newQuantity > 0) {
        clonedItems[itemIndex].quantity = newQuantity
      } else {
        clonedItems.splice(itemIndex, 1) // Remove item if quantity is 0 or less
      }

      // Map to API format
      const itemsToSubmit = clonedItems
        .map((item) => {
          const customizesAPIType: OrderCustomize[] = (
            item.customizes || []
          ).map((customize: any) => ({
            id: customize.id.toString(),
            name: customize.name,
            options: (customize.options || []).map((option: any) => ({
              id: option.id.toString(),
              name: option.name,
              price: option.price,
              quantity: option.quantity,
            })),
          }))
          return {
            item_id: item.item_id.toString(),
            quantity: item.quantity,
            price: item.price,
            note: item.note || "",
            customizes: customizesAPIType,
          }
        })
        .filter((item) => item.quantity > 0) // Filter again after potential splice and to ensure valid items

      return addRestaurantItems(
        userId.toString(),
        restaurantId.toString(),
        itemsToSubmit,
        { cartId: cartId.toString() } // Use the cartId passed in variables
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] })
      // toast.success("Số lượng cập nhật thành công (index)"); // Optional success message
    },
    onError: (error) => {
      toast.error("Lỗi cập nhật số lượng (index)", {
        description:
          error instanceof Error
            ? error.message
            : "Không thể cập nhật sản phẩm.",
      })
    },
  })

  // Toggle edit mode
  const toggleEditMode = () => {
    if (isEditing) {
      setIsEditing(false)
      setSelectedCarts([])
    } else {
      setIsEditing(true)
    }
  }

  // Toggle selected cart
  const toggleCartSelection = (cartId: number) => {
    setSelectedCarts((prev) =>
      prev.includes(cartId)
        ? prev.filter((id) => id !== cartId)
        : [...prev, cartId]
    )
  }

  // Select all carts
  const toggleSelectAll = () => {
    if (!cartResponse?.carts) return

    if (selectedCarts.length === cartResponse.carts.length) {
      setSelectedCarts([])
    } else {
      setSelectedCarts(cartResponse.carts.map((cart) => cart.cart_id))
    }
  }

  // Handle delete confirmation
  const confirmDelete = (cartIds: number[]) => {
    setCartsToDelete(cartIds)
    setDeleteDialogOpen(true)
  }

  // Execute delete
  const executeDelete = () => {
    deleteMutation.mutate(cartsToDelete)
  }

  // Cancel delete
  const cancelDelete = () => {
    setDeleteDialogOpen(false)
    setCartsToDelete([])
  }

  // Find cart by ID
  const getCartById = (cartId: number): RestaurantCart | undefined => {
    return cartResponse?.carts?.find((cart) => cart.cart_id === cartId)
  }

  // Function to update item quantity
  const updateItemQuantity = (
    restaurantId: number | string,
    itemId: number,
    newQuantity: number
  ) => {
    const currentCart = cartResponse?.carts?.find(
      (cart) => cart.restaurant_id === Number(restaurantId)
    )

    updateItemQuantityMutation.mutate({
      restaurantId,
      itemId,
      newQuantity,
      cartId: currentCart?.cart_id,
    })
  }

  // New action to update by index
  const updateItemQuantityByIndex = (
    restaurantId: string | number,
    itemIndex: number,
    newQuantity: number
  ) => {
    const currentCart = cartResponse?.carts?.find(
      (cart) => cart.restaurant_id === Number(restaurantId)
    )
    if (!currentCart || !currentCart.cart_id) {
      toast.error("Không tìm thấy giỏ hàng hoặc ID giỏ hàng để cập nhật.")
      return
    }
    updateItemQuantityByIndexMutation.mutate({
      restaurantId,
      itemIndex,
      newQuantity,
      cartId: currentCart.cart_id, // Pass the definite cartId
    })
  }

  // New Mutation for clearing a specific restaurant's cart
  const clearRestaurantCartMutation = useMutation({
    mutationFn: async (cartIdToClear: string) => {
      // Expects a single cart_id string
      if (!userId) {
        throw new Error("User not authenticated for clearing cart")
      }
      // clearCartAction expects an array of cart ID strings
      return clearCartAction(userId.toString(), [cartIdToClear])
    },
    onSuccess: () => {
      toast.success("Giỏ hàng của nhà hàng đã được xóa")
      queryClient.invalidateQueries({ queryKey: ["cart"] })
      // Potentially navigate away or update UI further if on that specific cart page
    },
    onError: (error) => {
      toast.error("Không thể xóa giỏ hàng của nhà hàng", {
        description: error instanceof Error ? error.message : "Đã xảy ra lỗi.",
      })
    },
  })

  // New action to clear a specific restaurant's cart
  const clearRestaurantCart = (restaurantIdToClear: string | number) => {
    if (!cartResponse?.carts) {
      return
    }
    const cartToClear = cartResponse.carts.find(
      (cart) => cart.restaurant_id === Number(restaurantIdToClear)
    )

    if (cartToClear && cartToClear.cart_id) {
      clearRestaurantCartMutation.mutate(cartToClear.cart_id.toString())
    }
  }

  return {
    // State
    isEditing,
    selectedCarts,
    deleteDialogOpen,
    cartsToDelete,

    // Actions
    toggleEditMode,
    toggleCartSelection,
    toggleSelectAll,
    confirmDelete,
    executeDelete,
    cancelDelete,
    getCartById,
    updateItemQuantity,
    updateItemQuantityByIndex,
    clearRestaurantCart,

    // Mutations
    deleteMutation,
    clearRestaurantCartMutation,
    updateItemQuantityMutation,
    updateItemQuantityByIndexMutation,
  }
}

export const useReconstructCartFromOrder = (orderId: string | null) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const userId = useAuthStore((state) => state.user?.user_id)

  const { data: order, isError: isOrderError } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => getOrderDetailClient(orderId!),
    enabled: !!orderId,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    placeholderData: keepPreviousData,
  })

  const restaurantId = order?.data?.restaurant?.restaurant_id?.toString()

  const { data: restaurant, isError: isRestaurantError } =
    useRestaurantDetailLongCache(restaurantId || null)

  const createDishMapFromRestaurantData = useCallback((restaurantData: any) => {
    if (!restaurantData?.data?.categories) return null

    const allDishes = restaurantData.data.categories.flatMap(
      (cat: any) => cat.items
    )
    return allDishes.reduce((acc: Record<number, MenuItem>, dish: MenuItem) => {
      acc[Number(dish.restaurant_item_id)] = dish
      return acc
    }, {})
  }, [])

  const cloneCartFromOrderMutation = useMutation({
    mutationKey: ["cloneCartFromOrder", orderId, restaurant?.dishMap],
    mutationFn: async () => {
      if (!orderId || !userId) {
        throw new Error("Missing required data: orderId or userId")
      }

      const order = await getOrderDetailClient(orderId)
      const restaurantId = order.data?.restaurant?.restaurant_id

      if (!restaurantId) {
        throw new Error("Không tìm thấy nhà hàng trong đơn hàng")
      }

      // Get fresh restaurant data
      const freshRestaurantData = await getRestaurantDetail(+restaurantId)
      const freshDishMap = createDishMapFromRestaurantData(freshRestaurantData)

      if (!freshDishMap) {
        throw new Error("Không thể tải thông tin nhà hàng")
      }

      const clonedItems = order.data?.restaurant_items || []
      const reconciledCart = reconcileCartWithDishMap(clonedItems, freshDishMap)

      // add the reconciled cart to the cart
      const response = await addRestaurantItems(
        userId.toString(),
        restaurantId.toString(),
        reconciledCart,
        { cartId: "0" }
      )

      if (!response?.cart_id) {
        throw new Error(
          response.message ?? "Không thể tạo giỏ hàng từ đơn hàng"
        )
      }

      // invalidate the cart query and redirect
      queryClient.invalidateQueries({ queryKey: ["cart"] })
      router.push(`/checkout?cart_id=${response.cart_id}&flow=normal`)

      return response
    },
    onError: (error: Error) => {
      toast.error("Không thể tạo giỏ hàng từ đơn hàng", {
        description: error.message,
      })
    },
  })

  return {
    cloneCartFromOrderMutation,
    isLoading: cloneCartFromOrderMutation.isPending,
    isError: isOrderError || isRestaurantError,
  }
}
